import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import axios from 'axios';
import { faShield } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Form from '../Form/Form';
import InputText from '../Form/Fields/InputText';
import Button from '../Form/Button';
import { API_BASE_URL } from '../../api';

import './CreateProductModal.scss';

function groupPermissions(permissions) {
    const groups = {};
    permissions.forEach(p => {
        const category = p.category || 'Autres';
        if (!groups[category]) groups[category] = [];
        groups[category].push(p);
    });
    return groups;
}

export default function CreateRoleModal({ show, onClose, onSaved, role = null }) {
    const isEdit = role != null;
    const [isLoading, setIsLoading] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [selected, setSelected] = useState(new Set());

    useEffect(() => {
        if (!show) return;
        axios.get(`${API_BASE_URL}/api/v1/admin/roles/permissions`, { withCredentials: true })
            .then(({ data }) => setPermissions(data))
            .catch(() => {});
    }, [show]);

    useEffect(() => {
        setSelected(new Set(role?.rightIds ?? []));
    }, [role]);

    const toggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleModule = (modulePerms) => {
        const ids = modulePerms.map(p => p.id);
        const allOn = ids.every(id => selected.has(id));
        setSelected(prev => {
            const next = new Set(prev);
            if (allOn) ids.forEach(id => next.delete(id));
            else ids.forEach(id => next.add(id));
            return next;
        });
    };

    const defaultValues = isEdit ? { code: role.code ?? '', name: role.name ?? '' } : { code: '', name: '' };

    const onSubmit = (data) => {
        const payload = { code: data.code, name: data.name, rightIds: [...selected] };
        const request = isEdit
            ? axios.put(`${API_BASE_URL}/api/v1/admin/roles/${role.id}`, payload, { withCredentials: true })
            : axios.post(`${API_BASE_URL}/api/v1/admin/roles`, payload, { withCredentials: true });

        setIsLoading(true);
        request
            .then(() => {
                toast.success(isEdit ? 'Rôle mis à jour.' : 'Rôle créé.');
                onSaved();
                onClose();
            })
            .catch(() => toast.error('Une erreur est survenue.'))
            .finally(() => setIsLoading(false));
    };

    const groups = groupPermissions(permissions);

    return (
        <Modal
            id="createRoleModal"
            title={isEdit ? 'Modifier le rôle' : 'Nouveau rôle'}
            show={show}
            onClose={onClose}
            icon={faShield}
            iconBg="#e0e7ff"
            iconColor="#4338ca"
            classSize="modal-lg"
            footer={
                <Button isLoading={isLoading} loadingMessage="Enregistrement ..." type="submit" form="roleForm" className="modal-btn-primary">
                    {isEdit ? 'Enregistrer' : 'Créer'}
                </Button>
            }
        >
            <Form id="roleForm" onSubmit={onSubmit} defaultValues={defaultValues} key={role?.id ?? 'new'}>
                <div className="cdm-section">
                    <p className="cdm-section__label">Informations</p>
                    <div className="row">
                        <div className="col-md-5">
                            <label className="cdm-field-label">Code <span className="text-danger">*</span></label>
                            <InputText
                                name="code"
                                placeholder="ex: CAISSIER"
                                validationRules={{ required: 'Le code est requis' }}
                            />
                        </div>
                        <div className="col-md-7">
                            <label className="cdm-field-label">Nom du rôle <span className="text-danger">*</span></label>
                            <InputText
                                name="name"
                                placeholder="ex: Caissier"
                                validationRules={{ required: 'Le nom est requis' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="cdm-section">
                    <p className="cdm-section__label">Permissions</p>

                    <div className="cdm-perm-list">
                        {Object.entries(groups).map(([category, perms]) => {
                            const allOn = perms.every(p => selected.has(p.id));
                            const someOn = perms.some(p => selected.has(p.id));
                            return (
                                <div key={category} className="cdm-perm-category">
                                    <div className="cdm-perm-header">
                                        <span className="cdm-perm-title">{category}</span>
                                        <label className="cdm-perm-toggle-all">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={allOn}
                                                ref={el => { if (el) el.indeterminate = someOn && !allOn; }}
                                                onChange={() => toggleModule(perms)}
                                            />
                                            Tout
                                        </label>
                                    </div>
                                    <div className="cdm-perm-grid">
                                        {perms.map(p => (
                                            <label key={p.id} className="cdm-perm-item">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selected.has(p.id)}
                                                    onChange={() => toggle(p.id)}
                                                />
                                                {p.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        {Object.keys(groups).length === 0 && (
                            <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                                Chargement des permissions…
                            </div>
                        )}
                    </div>
                </div>
            </Form>
        </Modal>
    );
}
